import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Button, Image } from "react-bootstrap";
import { GetAllProductAttributesWithValues } from "../Services/AttributeService";
import { AddProduct, UpdateProduct, GetProductById } from "../Services/ProductService";
import { GetAllCategories } from "../Services/CategoryService";
import { getImageUrl } from "../utils/imageUtils";
import bag_img from '../assets/images/bag_img.png';

export default function Addproduct({ onClose, product }) {
  const isEdit = !!product;

  const [form, setForm] = useState({
    productName: "",
    price: "",
    categoryId: "",
    description: "",
    vendorId: "",
    file: null,
    productImage: ""
  });

  const [attributes, setAttributes] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    const loginUserId = localStorage.getItem("userId");
    if (loginUserId) {
      setForm((prev) => ({ ...prev, vendorId: loginUserId }));
    }
  }, [product]);

  useEffect(() => {
    loadCategories();
    loadAttributes();
  }, []);

  useEffect(() => {
    if (categories.length > 0 && (form.categoryId === "" || form.categoryId === undefined)) {
      const firstCat = categories[0];
      setForm((prev) => ({ ...prev, categoryId: firstCat.id }));
      setSelectedCategoryName((firstCat.name || "").toLowerCase());
    }
  }, [categories]);

  async function loadAttributes() {
    try {
      const res = await GetAllProductAttributesWithValues();
      if (res.status && res.data) {
        setAttributes(res.data);
      }
    } catch (err) {
      console.log("Attribute Load Error:", err);
    }
  }

  const isWheat = selectedCategoryName === "wheat";

  async function loadCategories() {
    try {
      const res = await GetAllCategories({
        categoryNames: ["Wheat", "Grains", "Pulses"],
      });
      if (res.status && res.data) {
        setCategories(res.data);
      }
    } catch (err) {
      console.log("Category Load Error:", err);
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (isEdit) {
        try {
          const res = await GetProductById(product.id);
          if (res.status && res.data) {
            const data = res.data;

            let newFormState = {
              productName: data.name || "",
              price: data.price || "",
              categoryId: data.categoryId || "",
              description: data.description || "",
              file: null,
              productImage: data.path,
              vendorId: data.vendorId || localStorage.getItem("userId") || ""
            };

            if (data.categoryName) {
              setSelectedCategoryName(data.categoryName.toLowerCase());
            }

            const initialSelectedAttributes = {};

            if (data.productAttributes && Array.isArray(data.productAttributes)) {
              data.productAttributes.forEach((attr) => {
                const valueIds = attr.values.map((val) => val.id);
                initialSelectedAttributes[attr.id] = valueIds;

                const fieldName = toFieldName(attr.name);
                const valuesString = attr.values.map((val) => val.name).join(", ");
                newFormState[fieldName] = valuesString;
              });
            }

            setForm(newFormState);
            setSelectedAttributes(initialSelectedAttributes);
          }
        } catch (err) {
          console.error("Error fetching product details:", err);
        }
      }
    };

    fetchData();
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const getAttributeByName = (attributeName) => {
    if (!attributeName) return null;
    return attributes.find(
      (a) => (a.name || "").trim().toLowerCase() === attributeName.trim().toLowerCase()
    );
  };

  const handleAttributeSelect = (attributeName, selectedValueName, isMultiple = false) => {
    const attribute = getAttributeByName(attributeName);
    if (!attribute) return;

    const valueObj = (attribute.values || []).find(
      (v) => (v.name || "").trim().toLowerCase() === (selectedValueName || "").trim().toLowerCase()
    );
    if (!valueObj) return;

    // 1. Update IDs for API
    setSelectedAttributes((prev) => {
      const currentIds = prev[attribute.id] || [];
      let newSelectedIds;

      if (isMultiple) {
        if (currentIds.includes(valueObj.id)) {
          newSelectedIds = currentIds.filter((id) => id !== valueObj.id);
        } else {
          newSelectedIds = [...currentIds, valueObj.id];
        }
      } else {
        newSelectedIds = [valueObj.id];
      }

      return { ...prev, [attribute.id]: newSelectedIds };
    });

    // 2. Update Form Display Value
    const fieldName = toFieldName(attributeName);
    setForm((prev) => {
      if (isMultiple) {
        const currentString = prev[fieldName] || "";
        let currentNames = currentString ? currentString.split(", ") : [];

        if (currentNames.includes(valueObj.name)) {
          currentNames = currentNames.filter(n => n !== valueObj.name);
        } else {
          currentNames.push(valueObj.name);
        }
        return { ...prev, [fieldName]: currentNames.join(", ") };
      } else {
        return { ...prev, [fieldName]: valueObj.name };
      }
    });
  };

  const toFieldName = (label) => {
    return (label || "")
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .split(" ")
      .map((part, idx) => (idx === 0 ? part.toLowerCase() : part.charAt(0).toUpperCase() + part.slice(1)))
      .join("");
  };

  const buildAttributesPayload = () => {
    const payload = [];
    Object.keys(selectedAttributes).forEach((attrId) => {
      const attribute = attributes.find((a) => a.id === Number(attrId));
      if (!attribute) return;

      const selectedIds = selectedAttributes[attrId];
      if (!selectedIds || selectedIds.length === 0) return;

      const valuesList = [];
      selectedIds.forEach(id => {
        const valObj = (attribute.values || []).find(v => v.id === id);
        if (valObj) {
          valuesList.push({
            productAttributeValueId: valObj.id,
            increasePrice: valObj.increasePrice != null ? String(valObj.increasePrice) : "0"
          });
        }
      });

      if (valuesList.length > 0) {
        payload.push({
          attributeId: Number(attrId),
          values: valuesList
        });
      }
    });
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setLoading(true);

    const formData = new FormData();
    formData.append("name", form.productName);
    formData.append("description", form.description);
    formData.append("price", form.price);
    formData.append("categoryId", form.categoryId);
    formData.append("vendorId", form.vendorId);
    formData.append("isCustomProduct", selectedCategoryName === "custom product" ? "true" : "false");
    formData.append("attributes", JSON.stringify(buildAttributesPayload()));

    if (form.file) formData.append("file", form.file);

    let res;
    if (isEdit) {
      res = await UpdateProduct(product.id, formData);
    } else {
      res = await AddProduct(formData);
    }

    setLoading(false);

    if (res && res.status) {
      setSuccessMsg(`Product ${isEdit ? "updated" : "added"} successfully!`);
      setTimeout(() => {
        onClose();
      }, 1500);
    } else {
      setErrorMsg(res?.message || "Error saving product");
    }
  };

  const CustomDropdown = ({ label, name, value, options = [], onChange, isMultiple = false }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);

    const filteredOptions = (options || []).filter((opt) =>
      (opt || "").toLowerCase().includes(search.toLowerCase())
    );

    const isSelected = (opt) => {
      if (isMultiple) {
        const selectedArr = value ? value.split(", ") : [];
        return selectedArr.includes(opt);
      }
      return value === opt;
    };

    useEffect(() => {
      function handleClickOutside(event) {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [containerRef]);

    return (
      <Form.Group ref={containerRef} style={{ position: "relative" }} className="mb-3">
        <Form.Label>{label}</Form.Label>
        <div className="custom-select-with-icon">
          <input
            type="text"
            placeholder="Select..."
            readOnly
            value={value || ""}
            onClick={() => setOpen((s) => !s)}
            className="form-control"
            style={{ cursor: "pointer", textOverflow: 'ellipsis' }}
          />
          <span className="dropdown-icon">⏷</span>
        </div>

        {open && (
          <div className="custom-dropdown">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-control search-input"
              autoFocus
            />
            <div className="options-list">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <div
                    key={opt}
                    className="option-item d-flex align-items-center"
                    style={{ cursor: 'pointer', padding: '5px' }}
                    onClick={() => {
                      onChange({ target: { name, value: opt } });
                      if (!isMultiple) {
                        setSearch("");
                        setOpen(false);
                      }
                    }}
                  >
                    <input
                      type={isMultiple ? "checkbox" : "radio"}
                      name={isMultiple ? `${name}_multi` : name}
                      checked={isSelected(opt)}
                      readOnly
                      style={{ marginRight: 8, pointerEvents: 'none' }}
                    />
                    <span>{opt}</span>
                  </div>
                ))
              ) : (
                <div className="no-results">No results found</div>
              )}
            </div>
          </div>
        )}
      </Form.Group>
    );
  };

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <div className="addproduct-container">
        <div className="header d-flex justify-content-between align-items-center mb-4">
          <h4 className="fw-bold mb-0">{isEdit ? "Edit Product" : "Add Product"}</h4>
          <button type="button" onClick={onClose} className="close-btn">×</button>
        </div>

        {successMsg && (
          <div className="alert alert-success py-2">
            ✅ {successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="alert alert-danger py-2">
            ⚠️ {errorMsg}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <div className="d-flex align-items-center mb-4 gap-3">
            <div className="upload-circle">
              {form.file || form.productImage ? (
                <Image src={form.file ? URL.createObjectURL(form.file) : getImageUrl(form.productImage, bag_img)} roundedCircle className="preview-img" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: "0.75rem", textAlign: "center" }}>Upload</span>
              )}
            </div>
            <Form.Control
              type="file" name="file" accept="image/*"
              onChange={handleChange} style={{ display: "none" }} id="image-upload-input"
            />
            <label htmlFor="image-upload-input" style={{ cursor: "pointer" }}>Select Image</label>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Select Category</Form.Label>
            <div className="d-flex gap-4 mt-2">
              {categories.map((cat) => (
                <Form.Check
                  key={cat.id} inline type="radio" name="categoryId"
                  value={cat.id}
                  checked={String(form.categoryId) === String(cat.id)}
                  onChange={(e) => {
                    handleChange(e);
                    const found = categories.find((c) => String(c.id) === String(e.target.value));
                    setSelectedCategoryName((found?.name || "").toLowerCase());
                    setSelectedAttributes({});
                    setForm(prev => ({
                      ...prev,
                      categoryId: e.target.value
                    }));
                  }}
                  label={cat.name}
                  className="custom-radio"
                />
              ))}
            </div>
          </Form.Group>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Product Name</Form.Label>
                <Form.Control type="text" name="productName" value={form.productName} onChange={handleChange} required placeholder="Enter product name" />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Product Price (₹/Kg)</Form.Label>
                <Form.Control type="number" name="price" value={form.price} onChange={handleChange} required placeholder="Enter price" min="0" step="0.01" />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            {isWheat && (
              <>
                <Col>
                  <CustomDropdown
                    label="State"
                    name="state"
                    value={form.state}
                    options={(getAttributeByName("State")?.values || []).map((v) => v.name)}
                    onChange={(e) => handleAttributeSelect("State", e.target.value, false)}
                  />
                </Col>

                <Col>
                  <CustomDropdown
                    label="Grinding Texture"
                    name="grindingTexture"
                    value={form.grindingTexture}
                    isMultiple={true}
                    options={(getAttributeByName("Grinding Texture")?.values || []).map((v) => v.name)}
                    onChange={(e) => handleAttributeSelect("Grinding Texture", e.target.value, true)}
                  />
                </Col>

                <Col>
                  <CustomDropdown
                    label="Packaging"
                    name="packaging"
                    value={form.packaging}
                    isMultiple={true}
                    options={(getAttributeByName("Packaging")?.values || []).map((v) => v.name)}
                    onChange={(e) => handleAttributeSelect("Packaging", e.target.value, true)}
                  />
                </Col>
              </>
            )}
          </Row>

          <Row className="mb-3">
            {isWheat && (
              <Col>
                <CustomDropdown
                  label="Wheat Type"
                  name="wheatType"
                  value={form.wheatType}
                  options={(getAttributeByName("Wheat Type")?.values || []).map((v) => v.name)}
                  onChange={(e) => handleAttributeSelect("Wheat Type", e.target.value, false)}
                />
              </Col>
            )}

            {isWheat && (
              <Col>
                <CustomDropdown
                  label="Whole Wheat Flour"
                  name="wholeWheatFlour"
                  value={form.wholeWheatFlour}
                  options={(getAttributeByName("Whole Wheat Flour")?.values || []).map((v) => v.name)}
                  onChange={(e) => handleAttributeSelect("Whole Wheat Flour", e.target.value, false)}
                />
              </Col>
            )}

            <Col style={{ maxWidth: "50%" }}>
              <CustomDropdown
                label="Packaging Size"
                name="packagingSize"
                value={form.packagingSize}
                isMultiple={true}
                options={(getAttributeByName("Packaging Size")?.values || []).map((v) => v.name)}
                onChange={(e) => handleAttributeSelect("Packaging Size", e.target.value, true)}
              />
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label>Product Description</Form.Label>
            <Form.Control as="textarea" rows={3} name="description" value={form.description} onChange={handleChange} placeholder="Enter product description" />
          </Form.Group>

          <div className="text-end">
            <Button
              type="button"
              variant="outline-secondary"
              className="me-2"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" className="add-btn" disabled={loading}>
              {loading ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </span>
              ) : isEdit ? 'UPDATE PRODUCT' : 'ADD PRODUCT'}
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
}